import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import supabase from "../../supabase";
import AddUser from "./AddUser";
import { getAuthHeader } from "../../lib/authFetch";

const Members = ({ profile, organization }) => {
  const { companyId } = useParams();
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [inviteUser, setInviteUser] = useState(false);

  const toggleInviteMembers = () => {
    setInviteUser(!inviteUser);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setCurrentUser(profile);

        // Fetch all users in the same organization (includes avatar_url)
        const { data: organizationUsers, error: usersError } = await supabase
          .from("Users")
          .select("*")
          .eq("company_id", companyId);

        if (usersError) throw usersError;

        setUsers(organizationUsers);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchUsers();
    }
  }, [companyId, profile]);

  const deleteUser = async (user) => {
    const confirmed = window.confirm(
      `Are you sure you want to remove ${user.name || user.email}?`
    );
    if (!confirmed) return;

    // Deletion (including reassigning their projects/articles/messages and
    // removing the Users row) happens server-side in one authenticated call,
    // so it can't leave things half-deleted if a step fails partway through.
    let userId = user.id;
    try {
      const baseURL =
        process.env.NODE_ENV === "development"
          ? "http://localhost:5000"
          : window.location.origin;
      const response = await fetch(`${baseURL}/delete-user`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(await getAuthHeader()),
        },
        body: JSON.stringify({ userId }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error);
      }

      alert("User deleted successfully!");
      setUsers((prevUsers) => prevUsers.filter((u) => u.id !== user.id));
    } catch (error) {
      console.error("Error deleting user:", error.message);
      alert("Failed to delete user account.");
    }
  };

  const changeUserRole = async (userId, newRole) => {
    try {
      const { error } = await supabase
        .from("Users")
        .update({ role: newRole })
        .eq("id", userId);

      if (error) {
        throw new Error(error.message);
      }

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, role: newRole } : user
        )
      );

      alert("User role updated successfully!");
    } catch (error) {
      console.error("Error updating user role:", error.message);
      alert("Failed to update user role.");
    }
  };

  if (loading) return <div style={{ padding: 40 }}>Loading members...</div>;
  if (error) return <div style={{ padding: 40 }}>Error: {error}</div>;

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.breadcrumbs}>Members</div>
          <h1 style={styles.title}>{organization?.name} Members</h1>
        </div>

        {currentUser?.role === "Admin" && (
          <button style={styles.inviteButton} onClick={toggleInviteMembers}>
            + Invite Member
          </button>
        )}
      </div>

      {inviteUser && (
        <div style={styles.inviteWrap}>
          <AddUser
            profile={profile}
            organization={organization}
            onCancel={toggleInviteMembers}
          />
        </div>
      )}

      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: "35%" }}>Name</th>
              <th style={{ ...styles.th, width: "35%" }}>Email</th>
              <th style={{ ...styles.th, width: "20%" }}>Role</th>
              <th style={{ ...styles.th, width: "10%", textAlign: "right" }}>
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {users.map((user) => (
              <tr key={user.id} style={styles.tr}>
                {/* Name */}
                <td style={styles.td}>
                  <div style={styles.nameCell}>
                    {/* ✅ Avatar Logic: Image or Initial */}
                    <div style={styles.avatar}>
                      {user.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt={user.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        (user.name || "?").slice(0, 1).toUpperCase()
                      )}
                    </div>
                    <div style={styles.nameText}>{user.name}</div>
                  </div>
                </td>

                {/* Email */}
                <td style={styles.td}>
                  <span style={styles.email}>{user.email}</span>
                </td>

                {/* Role */}
                <td style={styles.td}>
                  {currentUser?.role === "Admin" &&
                  user.id !== currentUser.id ? (
                    <select
                      style={styles.roleSelect}
                      value={user.role}
                      onChange={(e) => changeUserRole(user.id, e.target.value)}
                    >
                      <option value="Admin">Admin</option>
                      <option value="Writer">Writer</option>
                      <option value="Editor">Editor</option>
                      <option value="Approver">Approver</option>
                    </select>
                  ) : (
                    <span style={styles.roleText}>{user.role}</span>
                  )}
                </td>

                {/* Actions */}
                <td style={{ ...styles.td, textAlign: "right" }}>
                  {currentUser?.role === "Admin" &&
                    user.id !== currentUser.id && (
                      <button
                        style={styles.trashButton}
                        onClick={() => deleteUser(user)}
                        title="Remove Member"
                      >
                        <TrashIcon size={16} />
                      </button>
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div style={styles.emptyState}>No members found.</div>
        )}
      </div>
    </div>
  );
};

const TrashIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M9 3h6m-7 4h8m-9 0h10l-1 14a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2L7 7Z"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 11v7M14 11v7"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const styles = {
  page: {
    padding: "28px 28px 40px",
    background: "white",
    minHeight: "100vh",
  },

  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 18,
  },

  breadcrumbs: {
    fontSize: 13,
    color: "#6b7280",
    marginBottom: 6,
  },

  title: {
    fontSize: 24,
    fontWeight: 700,
    color: "#111827",
    margin: 0,
  },

  inviteButton: {
    background: "#00876e",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
  },

  inviteWrap: {
    marginBottom: 16,
  },

  card: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
  },

  th: {
    textAlign: "left",
    fontSize: 12,
    fontWeight: 700,
    color: "#6b7280",
    padding: "14px 18px",
    borderBottom: "1px solid #e5e7eb",
    background: "#ffffff",
  },

  tr: {
    borderBottom: "1px solid #eef2f7",
  },

  td: {
    padding: "14px 18px",
    fontSize: 14,
    color: "#111827",
    verticalAlign: "middle",
  },

  nameCell: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },

  avatar: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: "#111827",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 14,
    overflow: "hidden", // ✅ Ensuring image clips to circle
  },

  nameText: {
    fontWeight: 700,
  },

  email: {
    color: "#374151",
  },

  roleSelect: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "8px 10px",
    background: "#f9fafb",
    fontWeight: 600,
    cursor: "pointer",
  },

  roleText: {
    fontWeight: 600,
    color: "#374151",
  },

  trashButton: {
    width: 40,
    height: 36,
    borderRadius: 10,
    border: "none",
    background: "#e11d48",
    color: "#fff",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 6px 16px rgba(225, 29, 72, 0.25)",
  },

  emptyState: {
    padding: 18,
    color: "#6b7280",
    fontSize: 14,
  },
};

export default Members;
